import React from 'react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';

const V2InternationalP3 = () => {
  useSEO({
    title: 'International P3 & PPP | UK experience, local configuration',
    description: 'Keystone brings UK PFI, Hard Services and lifecycle experience to international P3 and PPP concessions. Configure Videra around your local contract and operating model.',
    path: '/international-p3',
  });

  return (
    <div data-testid="v2-international-p3">
      <ProductHero
        eyebrow="International P3 / PPP"
        title="Long-term infrastructure contracts don't become easier with age."
        subtitle="UK PFI experience. International P3 application."
        body={(
          <>
            <p>Keystone brings practical experience from mature UK PFI, Hard Services and lifecycle environments to organisations operating long-term P3 and PPP infrastructure internationally.</p>
            <p>The terminology and contractual structures may differ. Many of the operational challenges are familiar: contractual obligations, asset condition, maintenance, lifecycle investment, evidence, operational assurance, risk, concession maturity and handback.</p>
            <p>Our approach is not to impose a UK contractual model on another country. We bring the experience, methodology and technology, then configure the environment around the customer&rsquo;s local contract, assets, standards, terminology and operating model.</p>
          </>
        )}
        primary={{ label: 'Discuss International P3 with Keystone', to: '/contact' }}
        heroImage="/images/healthcare/hc-asset-register.jpg"
        heroImageAlt="Videra international P3 asset assurance"
        testid="international-hero"
      />

      <section className="py-16 md:py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Experience + Methodology + Technology</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Experience', body: 'Lessons from complex UK PFI and lifecycle environments.' },
              { title: 'Methodology', body: 'Structured project, lifecycle, assurance and handback approaches.' },
              { title: 'Technology', body: 'Configurable Videra environments.' },
            ].map((c) => (
              <div key={c.title} className="bg-white border border-slate-200 rounded-xl p-6">
                <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">{c.title}</p>
                <p className="text-slate-700 text-sm leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Canada section (per brief) */}
      <section className="py-20 md:py-28" data-testid="international-canada">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">Canada</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
            A mature P3 market entering its handback decade.
          </h2>
          <div className="mt-8 text-base lg:text-lg text-slate-700 leading-relaxed space-y-4">
            <p>Canada has an established P3 infrastructure market containing long-term operating, maintenance and lifecycle responsibilities across major public assets.</p>
            <p>As concessions mature, organisations need clear visibility of contractual obligations, asset condition, lifecycle intervention, evidence and future handback requirements.</p>
            <p>Keystone can apply lessons from mature UK PFI environments while configuring Videra around the individual Canadian P3 contract and operating model.</p>
          </div>
          <p className="mt-8 text-sm text-slate-600">Potential users include:</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {['ProjectCos','SPVs','FM providers','O&M providers','Infrastructure asset managers','Healthcare P3 operators','Concession managers','P3 advisers'].map((s) => (
              <span key={s} className="text-xs bg-slate-100 text-slate-700 rounded-full px-3 py-1.5">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default V2InternationalP3;
